import React, { useEffect, useState, useRef } from "react";
import { TextField, Box, MenuItem } from "@mui/material";
import { loadGoogleMapsScript } from "../../utils/loadGoogleMaps";

const apiKey = process.env.REACT_APP_GOOGLE_PLACES_KEY;

export default function AddressAutoComplete({
  address1,
  setAddress1,
  setCity,
  setState,
  setCountry,
  setZip,
  error,
  helperText,
  required,
}) {
  const [mapsReady, setMapsReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const autocompleteService = useRef(null);
  const geocoder = useRef(null);

  // ✅ Load the Google Maps script
  useEffect(() => {
    loadGoogleMapsScript(apiKey).then(() => {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      geocoder.current = new window.google.maps.Geocoder();
      setMapsReady(true);
    });
  }, []);

  // ✅ Handle typing in the address field
  const handleInputChange = async (e) => {
    const value = e.target.value;
    setAddress1(value);

    if (!value) {
      setSuggestions([]);
      return;
    }

    if (!autocompleteService.current) return;

    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      { input: value, types: ["address"] },
      (predictions, status) => {
        setLoading(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  // ✅ Handle address selection
  const handleSelect = async (prediction) => {
    setSuggestions([]);

    if (!geocoder.current) return;

    geocoder.current.geocode({ placeId: prediction.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const components = results[0].address_components;
        const getComponent = (type) =>
          components.find((c) => c.types.includes(type))?.long_name || "";

        // Extract street-level details only
        const streetNumber = getComponent("street_number");
        const route = getComponent("route");
        const streetAddress = `${streetNumber} ${route}`.trim();

        // ✅ Update all fields
        setAddress1(streetAddress);
        setCity(getComponent("locality") || getComponent("sublocality") || "");
        setState(getComponent("administrative_area_level_1") || "");
        setCountry(getComponent("country") || "");
        setZip(getComponent("postal_code") || "");
      }
    });
  };

  if (!mapsReady) {
    return <Box sx={{ py: 2 }}>Loading address autocomplete...</Box>;
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <TextField
        value={address1}
        onChange={handleInputChange}
        label="Address"
        fullWidth
        error={error}
        helperText={helperText}
        required={required}
        variant="standard"
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            bgcolor: "#fff",
            border: "1px solid #eee",
            borderRadius: 1,
            mt: 0.5,
            maxHeight: 200,
            overflowY: "auto",
          }}
        >
          {loading && <MenuItem disabled>Loading...</MenuItem>}
          {suggestions.map((suggestion) => (
            <MenuItem
              key={suggestion.place_id}
              onClick={() => handleSelect(suggestion)}
            >
              {suggestion.description}
            </MenuItem>
          ))}
        </Box>
      )}
    </Box>
  );
}
